import styles from 'css/src/components/common/PreviewModalShell.module.scss';

import React from 'react';
import classNames from 'classnames';

import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';

type PreviewModalShellProps = {
    showing: boolean;
    onHidden: VoidFunction;
    title?: string | React.ReactNode;
    /**
     * Big preview content (video, image, etc.) shown in the left column.
     */
    preview: React.ReactNode;
    /**
     * Optional content rendered below the preview in the left column —
     * e.g. the template scene-previews grid + "Preview all scenes" toggle.
     * Hidden in the stacked (narrow-viewport) layout.
     */
    belowPreview?: React.ReactNode;
    /**
     * Controls column content — Format pills, Brand kit dropdown, prompt
     * input, etc.
     */
    controls: React.ReactNode;
    /**
     * Optional footer inside the controls column, pinned to the bottom when
     * `fixedHeight` is true. Typically the "Alternative workflows" block.
     */
    controlsFooter?: React.ReactNode;
    /**
     * Bootstrap Modal.Footer content (rendered in the modal chrome below the
     * body). Pass null/undefined to omit.
     */
    footer?: React.ReactNode;
    /**
     * When set, locks the body to this CSS height so the controls column
     * stays put and `controlsFooter` pins to its bottom while the preview
     * column scrolls. When omitted, the modal grows naturally with content.
     */
    fixedHeight?: string;
    /** Render the same two-column layout as a block on the page instead of inside modal chrome. */
    inline?: boolean;
};

const PreviewModalShell: React.FC<PreviewModalShellProps> = ({
    showing,
    onHidden,
    title,
    preview,
    belowPreview,
    controls,
    controlsFooter,
    footer,
    fixedHeight,
    inline,
}) => {
    const body = (
        <div
            className={classNames(styles['shell-grid'], {
                [styles['shell-grid--fixed-height']]: fixedHeight,
            })}
            style={fixedHeight ? { height: fixedHeight } : undefined}
        >
            <div className={styles['shell-preview']}>
                <div className={styles['shell-preview-content']}>{preview}</div>
                {belowPreview && (
                    <div className={styles['shell-below-preview']}>{belowPreview}</div>
                )}
            </div>
            <div className={styles['shell-controls']}>
                <div className={styles['shell-controls-content']}>{controls}</div>
                {controlsFooter && (
                    <div className={styles['shell-controls-footer']}>{controlsFooter}</div>
                )}
            </div>
        </div>
    );

    if (inline) {
        return (
            <div className={styles['shell-inline']}>
                {body}
                {footer && <div className={styles['shell-inline-footer']}>{footer}</div>}
            </div>
        );
    }

    return (
        <Modal
            showing={showing}
            onHidden={onHidden}
            title={title}
            size={MODAL_SIZES.XL}
            footer={footer ?? null}
            scrollable
        >
            {body}
        </Modal>
    );
};

export default PreviewModalShell;
